import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import FormLabel from 'react-bootstrap/esm/FormLabel';
import Image from 'react-bootstrap/esm/Image';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import {
  getUserDataToAddToTeam,
  getUserDataToUpdate,
  updateUser,
} from '../services/userService';
import { showError, showSuccess } from '../utils/toast';

const normalizeId = (value) => String(value || '');

const isActiveUser = (user) => user?.Status !== 'InActive';

function UpdateUser({ embedded = false, onClose, onSuccess, modalPayload }) {
  const { id: routeId } = useParams();
  const id = embedded && modalPayload?.id ? modalPayload.id : routeId;
  const navigate = useNavigate();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [userValue, setUserValue] = useState({
    id,
    name: '',
    Code: '',
    email: '',
    status: '',
    usertype: '',
    profile: '',
  });

  const loadTeamOptions = useCallback(async () => {
    if (!id) return;
    setLoadingTeam(true);
    try {
      const data = await getUserDataToAddToTeam(id);
      const availableUsers = [
        ...(data.teamMembers || []),
        ...(data.nonTeamMembers || []),
      ].filter(isActiveUser);
      setUsersData(availableUsers);
    } catch (error) {
      console.error('Error fetching team options:', error);
      setUsersData([]);
      showError(error.message || 'Unable to load recruiters for team assignment.');
    } finally {
      setLoadingTeam(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    getUserDataToUpdate(id)
      .then((res) => {
        const user = res.user || res;
        const existingTeam = (res.teamMembers || [])
          .filter(isActiveUser)
          .map((member) => normalizeId(member._id));
        setUserValue({
          id,
          name: user.EmployeeName || '',
          Code: user.EmpCode || '',
          email: user.Email || '',
          status: user.Status || '',
          usertype: user.UserType || '',
          profile: user.ProfilePic || '',
        });
        setSelectedUsers(existingTeam);
      })
      .catch((err) => {
        console.error(err);
        showError(err.message || 'Unable to load user details.');
      });
  }, [id]);

  useEffect(() => {
    if (userValue.usertype === 'TeamLead') {
      loadTeamOptions();
    } else {
      setUsersData([]);
      setSelectedUsers([]);
    }
  }, [userValue.usertype, loadTeamOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedUserData = {
      ...userValue,
      ...(userValue.usertype === 'TeamLead' ? { Team: selectedUsers } : {}),
    };

    try {
      const response = await updateUser(id, updatedUserData);
      if (embedded && onSuccess) onSuccess(response.msg || 'User updated successfully');
      else if (embedded && onClose) onClose();
      else {
        showSuccess(response.msg || 'User updated successfully');
        navigate('/users/list');
      }
    } catch (err) {
      console.error(err);
      showError(err.message || 'Error updating user.');
    }
  };

  const handleUserSelection = (e) => {
    const userId = normalizeId(e.target.value);
    const isChecked = e.target.checked;

    setSelectedUsers((prevSelectedUsers) => {
      if (isChecked) {
        return prevSelectedUsers.includes(userId)
          ? prevSelectedUsers
          : [...prevSelectedUsers, userId];
      }
      return prevSelectedUsers.filter((item) => item !== userId);
    });
  };

  const isTeamLead = userValue.usertype === 'TeamLead';

  return (
    <div className={embedded ? 'hr-embedded-form' : ''}>
      <Form
        id="edit-user-form"
        onSubmit={handleSubmit}
        style={embedded ? {} : { backgroundColor: 'aliceblue', padding: '30px', borderRadius: '20px', margin: '15px' }}
      >
        {!embedded && (
          <Image
            src={`${API_BASE_URL}/${userValue.profile}`}
            style={{ width: '100px', borderRadius: '100px' }}
            alt="Profile"
          />
        )}

        {!embedded && (
          <h3 style={{ textAlign: 'center', fontFamily: 'initial' }}>
            <b>
              <img style={{ width: '35px', borderRadius: '50px' }} src="/Images/icon.png" alt="icon" />
              {' '}
              Update {userValue.usertype || 'User'}
            </b>
          </h3>
        )}
        {!embedded && <hr />}

        <Row>
          <Col>
            <FormLabel><b>Emp Code</b></FormLabel>
            <Form.Control
              value={userValue.Code}
              onChange={(e) => setUserValue({ ...userValue, Code: e.target.value })}
              style={{ width: '300px', borderRadius: '15px', border: '1px solid black' }}
              placeholder="Employee ID"
            />
          </Col>
          <Col>
            <FormLabel><b>Employee Name</b></FormLabel>
            <Form.Control
              value={userValue.name}
              onChange={(e) => setUserValue({ ...userValue, name: e.target.value })}
              style={{ width: '300px', borderRadius: '15px', border: '1px solid black' }}
              placeholder="Employee Name"
            />
          </Col>
          <Col>
            <FormLabel><b>Email ID</b></FormLabel>
            <Form.Control
              type="email"
              value={userValue.email}
              onChange={(e) => setUserValue({ ...userValue, email: e.target.value })}
              style={{ width: '300px', borderRadius: '15px', border: '1px solid black' }}
              placeholder="Email ID"
            />
          </Col>
          <Col>
            <FormLabel><b>User Type</b></FormLabel>
            <Form.Select
              value={userValue.usertype}
              onChange={(e) => setUserValue({ ...userValue, usertype: e.target.value })}
              style={{ width: '300px', borderRadius: '15px', border: '1px solid black' }}
            >
              <option value="">Choose...</option>
              <option value="User">User</option>
              <option value="TeamLead">TeamLead</option>
              <option value="Admin">Admin</option>
            </Form.Select>
          </Col>
          <Col>
            <FormLabel><b>Status</b></FormLabel>
            <Form.Select
              value={userValue.status}
              onChange={(e) => setUserValue({ ...userValue, status: e.target.value })}
              style={{ width: '300px', borderRadius: '15px', border: '1px solid black' }}
            >
              <option value="">Choose...</option>
              <option value="Active">Active</option>
              <option value="InActive">Inactive (Left company)</option>
            </Form.Select>
            {userValue.status === 'InActive' && (
              <p className="hr-page-hint mt-2 mb-0">
                Inactive users cannot log in and are hidden from team assignment lists.
              </p>
            )}
          </Col>
        </Row>

        {isTeamLead && (
          <>
            <hr />
            <div className="hr-section-toolbar">
              <h5 className="mb-0">Assign Recruiters to Team Lead</h5>
              <span className="text-muted">{selectedUsers.length} selected</span>
            </div>
            <p className="hr-page-hint">
              Select recruiters (User role) who report to this Team Lead, then click Save.
            </p>

            {loadingTeam ? (
              <p className="hr-empty-state">Loading recruiters...</p>
            ) : usersData.length === 0 ? (
              <p className="hr-empty-state">No recruiters available to assign.</p>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover className="hr-table table-sm">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Employee Name</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData.map((user) => {
                      const userId = normalizeId(user._id);
                      return (
                        <tr key={userId}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              value={userId}
                              onChange={handleUserSelection}
                              checked={selectedUsers.includes(userId)}
                            />
                          </td>
                          <td>{user.EmployeeName}</td>
                          <td>{user.Email}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
            <hr />
          </>
        )}

        {!embedded && (
          <center>
            <Button
              type="submit"
              style={{ borderRadius: '15px', margin: '20px', backgroundColor: 'green', border: '1px solid black' }}
            >
              <b>Update</b>
            </Button>
          </center>
        )}
      </Form>
    </div>
  );
}

export default UpdateUser;
